import React, {useEffect, useState, useContext, useRef, useMemo} from 'react';
import {
  View,
  FlatList,
  TouchableOpacity,
  Pressable,
  ScrollView,
  Platform,
} from 'react-native';

import Text from '../../elements/Text';
import ProductCard from '../ProductCard';
import CatalogProductCard from '../CatalogProductCard';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../../theme';
import {fetchApi, PAGE_SIZE} from '../../../utils/ApiHelper';
import {HTTP_METHODS, PRODUCTS} from '../../../utils/URLConstants';
import {errorStatusContext} from '../../../contexts/ErrorStatusContext';
import {loadingPauseContext} from '../../../contexts/LoadingPauseContext';
import {FlatGrid} from 'react-native-super-grid';
import PageView from '../../elements/PageView';
import CommonStyles from '../../../utils/CommonStyles';
import {tID} from '../../../utils/TestIdHelper';
import {CloseIcon, EmptyFeed} from '../../../utils/SvgIcons';

const ProductCatalog = ({
  navigation,
  searchTerm,
  searchFilterEnabled,
  selectedFilters,
  setSearchFilterEnabled,
  setSelectedFilters,
  setProductBeingViewed,
  ...props
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const tagArray = ['All', 'Free For Me']; // commented 'Purchase Available'
  const [pageCount, setPageCount] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTag, setSelectedTag] = useState(0);
  const [rows, setRows] = useState([]);

  const didLoad = useRef(false);
  const searchTimeout = useRef(null);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  const isWebPlatform = useMemo(() => {
    return Platform.OS === 'web';
  }, []);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch only on tag/page change; getData is recreated each render and would refetch on every render
  }, [selectedTag, currentPage]);

  const getData = () => {
    const tagArrayForAPI = ['all', 'freeForMe'];
    let params = {
      page: currentPage,
      size: pageSize,
      type: tagArrayForAPI[selectedTag],
    };

    if (selectedFilters && selectedFilters.length > 0) {
      let priceRange = selectedFilters[0];
      let tag = selectedFilters[1];
      if (priceRange.length > 0) {
        params['startPriceRange'] = priceRange[0];
        params['endPriceRange'] = priceRange[1];
      }
      if (tag.length > 0) {
        params['type'] = tagArrayForAPI[tag];
      }
    }
    if (searchTerm && searchTerm.length > 0) {
      params['search'] = searchTerm;
    }

    lpc?.setVisible(true);
    fetchApi(HTTP_METHODS.GET, PRODUCTS, null, params)
      .then(res => {
        didLoad.current = true;
        lpc.setVisible(false);
        setRows(res.data.data.rows);
        setPageCount(Math.ceil(res.data.data.count / pageSize));
      })
      .catch(err => {
        didLoad.current = true;
        lpc.setVisible(false);
        esc.showError(err);
      });
  };

  useEffect(() => {
    if (didLoad.current) {
      setSearchFilterBool('');
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when filters change; getData/setSearchFilterBool are recreated each render
  }, [selectedFilters]);

  useEffect(() => {
    if (didLoad.current) {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
        searchTimeout.current = null;
      }

      searchTimeout.current = setTimeout(() => {
        setSearchFilterBool(searchTerm);
        getData();
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced fetch keyed to searchTerm only; adding per-render getData/setSearchFilterBool would bypass the debounce
  }, [searchTerm]);

  const setSearchFilterBool = searchText => {
    if (
      (searchText && searchText.length > 0) ||
      (selectedFilters && selectedFilters.length > 0)
    ) {
      if (!searchFilterEnabled) {
        setSearchFilterEnabled(true);
      }
    } else {
      if (searchFilterEnabled) {
        setSearchFilterEnabled(false);
      }
    }
  };

  const onChangePage = page => {
    setCurrentPage(page);
  };

  const onPressCategory = (item, index) => {
    setSelectedTag(index);
  };

  function navigateToViewProduct(product) {
    if (isWebPlatform) {
      // yet to develop this screen
      // navigation('/view-product', {state: {product}});
      setProductBeingViewed(product);
    } else {
      navigation.navigate('ViewProduct', {product});
    }
  }

  const renderItem = ({item, index}) => {
    return (
      <Pressable onPress={() => navigateToViewProduct(item)}>
        {isWebPlatform ? (
          <CatalogProductCard product={item} />
        ) : (
          <ProductCard product={item} />
        )}
      </Pressable>
    );
  };

  const renderTagList = () => {
    return (
      <FlatList
        testID={tID('ProductCatalog-FlatList-TagsList')}
        horizontal
        showsHorizontalScrollIndicator={false}
        data={tagArray}
        renderItem={renderTags}
        keyExtractor={(item, index) => index}
        showsVerticalScrollIndicator={false}
        style={styles.marginTop16}
      />
    );
  };

  const renderTags = ({item, index}) => {
    return (
      <TouchableOpacity
        testID={tID(`ProductCatalog-TouchableOpacity-Tag${index + 1}`)}
        onPress={() => onPressCategory(item, index)}
        activeOpacity={0.8}
        style={styles.tagContainer(index)}>
        <Text
          testID={tID(`ProductCatalog-Text-Tag${index + 1}`)}
          variation={selectedTag == index ? 'Text1Bold' : 'Text1Regular'}
          color={colors.textMuted}>
          {item}
        </Text>
      </TouchableOpacity>
    );
  };

  const renderFilterTag = (item, index) => {
    if (item.length == 0) {
      return null;
    }
    return (
      <View
        testID={tID(`ProductCatalog-View-FilterTag${index + 1}`)}
        key={index}
        style={[styles.tagContainer(index), styles.flexRow]}>
        {index == 1 && (
          <Text
            testID={tID(`ProductCatalog-Text-FilterTag${index + 1}`)}
            variation={'Text1SemiBold'}
            color={colors.textPrimary}>
            {tagArray[item[0]]}
          </Text>
        )}
        {index == 0 && (
          <Text
            testID={tID(`ProductCatalog-Text-FilterTagPrice${index + 1}`)}
            variation={'Text1Regular'}
            color={colors.textPrimary}>
            Price:{' '}
            <Text
              testID={tID(`ProductCatalog-Text-FilterTagAmount${index + 1}`)}
              variation={'Text1SemiBold'}
              color={colors.textPrimary}>
              $ {item[0].toFixed(2)} - {item[1].toFixed(2)}
            </Text>
          </Text>
        )}
        <Pressable
          testID={tID(`ProductCatalog-Pressable-FilterTagClose${index + 1}`)}
          hitSlop={styles.hitSlop}
          onPress={() => {
            const filters = [...selectedFilters];
            filters[index] = [];
            setSelectedFilters(filters);

            if (filters[0].length == 0 && filters[1].length == 0) {
              setSelectedFilters([]);
            }
          }}>
          <CloseIcon
            testID={tID(`ProductCatalog-Image-FilterTagClose${index + 1}`)}
            width={12}
            height={12}
            tintColor={colors.textDisabled}
            style={CommonStyles.ml_16}
          />
        </Pressable>
      </View>
    );
  };

  const renderFilterTags = () => {
    let activeFilters = 0;
    if (selectedFilters && selectedFilters.length > 0) {
      if (selectedFilters[0].length > 0) {
        activeFilters += 1;
      }

      if (selectedFilters[1].length > 0) {
        activeFilters += 1;
      }
    }
    return (
      <View
        testID={tID('ProductCatalog-View-FilterTagsContainer')}
        style={styles.marginHorizontal24}>
        <Text
          testID={tID('ProductCatalog-Text-ResultsTitle')}
          variation="Subtitle1SemiBold"
          color={colors.textHeading}
          topMargin={26}>
          {activeFilters > 0
            ? 'Active Filters'
            : rows.length
              ? 'Search Results '
              : 'No Results '}
          <Text
            testID={tID('ProductCatalog-Text-ResultsCount')}
            variation="Subtitle1Regular"
            color={colors.textHeading}>
            ({rows.length})
          </Text>
        </Text>
        <ScrollView
          testID={tID('ProductCatalog-ScrollView-Tags')}
          horizontal
          style={styles.marginTop16}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}>
          {selectedFilters.map((item, index) => {
            return renderFilterTag(item, index);
          })}
        </ScrollView>
      </View>
    );
  };

  const renderEmptyListView = () => {
    return (
      <View style={styles.emptyListContainer}>
        <EmptyFeed testID={tID('ProductCatalog-Image-Empty')} />
      </View>
    );
  };

  return (
    <View
      style={styles.flex1}
      testID={tID('ProductCatalog-View-MainConatiner')}>
      {!searchFilterEnabled && (
        <View style={!isWebPlatform && styles.marginHorizontal24}>
          {renderTagList()}
        </View>
      )}
      {searchFilterEnabled && renderFilterTags()}
      {isWebPlatform ? (
        <FlatGrid
          testID={tID('ProductCatalog-FlatGrid-Products')}
          data={rows}
          renderItem={renderItem}
          itemDimension={527}
          spacing={24}
          ListEmptyComponent={renderEmptyListView}
          style={styles.mhMinus24}
        />
      ) : (
        <FlatList
          testID={tID('ProductCatalog-FlatList-Products')}
          data={rows}
          keyExtractor={(item, index) => index}
          renderItem={renderItem}
          ListEmptyComponent={renderEmptyListView}
          contentContainerStyle={styles.paddingBottom24}
        />
      )}
      {pageCount > 1 && (
        <View
          testID={tID('ProductCatalog-View-PageViewContainer')}
          style={[
            styles.pageViewContainer,
            isWebPlatform && CommonStyles.self_center,
          ]}>
          <PageView
            pageCount={pageCount}
            currentIndex={currentPage}
            onPressPage={page => onChangePage(page)}
            onPressPrevious={() => onChangePage(currentPage - 1)}
            onPressNext={() => onChangePage(currentPage + 1)}
          />
        </View>
      )}
    </View>
  );
};

export default ProductCatalog;
